import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  getStoredUser, isLoggedIn, login as apiLogin, logout as apiLogout, me, onAuthChange,
  type AuthUser, type LoginResponse, type Role,
} from '../api/auth';

interface AuthValue {
  user: AuthUser | null;
  role: Role | null;
  isAdmin: boolean;
  /** 기동 직후 /auth/me 확인이 끝날 때까지 true. 라우트 가드는 이 동안 판단을 미룬다. */
  loading: boolean;
  login: (userId: string, password: string) => Promise<LoginResponse>;
  /** 로그아웃 — 로그인을 지운 뒤의 세대를 돌려준다(끝났을 때 세대가 그대로일 때만 알림 · 이동 — 13회차 N20) */
  logout: () => Promise<number>;
  refreshMe: () => Promise<AuthUser | null>;
}

const Ctx = createContext<AuthValue | null>(null);

/**
 * 세션 상태를 앱 전체에 공급한다.
 *
 * 스토어(sessionStorage)에 사용자가 있으면 즉시 그 값으로 그리고, 뒤에서
 * /auth/me 로 확인한다 — 새로고침마다 상단바가 "로그인" 으로 깜빡이지 않게.
 * 어떤 요청에서든 세션이 끊기면 스토어가 'aitex:auth' 를 쏘고 여기서 받는다.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [loading, setLoading] = useState<boolean>(() => isLoggedIn());

  useEffect(() => onAuthChange(() => setUser(getStoredUser())), []);

  const refreshMe = useCallback(async () => {
    if (!isLoggedIn()) { setUser(null); return null; }
    try {
      await me();
      // 14회차 Codex 6차 N20: 응답 값이 아니라 저장소에서 — me() 가 저장한 뒤 깨어나는 사이 새 로그인이 끝났으면 저장소가 곧 새 세션이다
      const u = getStoredUser();
      setUser(u);
      return u;
    } catch {
      // 401 은 authFetch 가 스토어를 비우고 이벤트를 쏜다. 그 외(네트워크)는 저장값 유지.
      // 그사이 세대가 바뀌었으면(StaleAuthError) me() 가 저장하지 않았다 — 지금 저장소가 곧 지금 세션이다
      const u = getStoredUser();
      setUser(u);
      return u;
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) { setLoading(false); return; }
    let alive = true;
    void refreshMe().finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshMe]);

  // 사용자 상태는 응답이 아니라 저장소에서 읽는다 — 늦게 끝난 로그인 · 로그아웃이 그 뒤의 새 세션 사용자를 덮거나 지우지 않게(N20)
  const login = useCallback(async (id: string, pw: string) => {
    const r = await apiLogin(id, pw);
    setUser(getStoredUser());
    return r;
  }, []);

  const logout = useCallback(async () => {
    const g = await apiLogout();
    setUser(getStoredUser());
    return g;
  }, []);

  const value = useMemo<AuthValue>(() => ({
    user,
    role: user?.role ?? null,
    isAdmin: user?.role === 'admin',
    loading,
    login,
    logout,
    refreshMe,
  }), [user, loading, login, logout, refreshMe]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth 는 <AuthProvider> 안에서만 쓸 수 있습니다');
  return v;
}
